import { useEffect, useState } from 'react';
import { AccountLayout } from '@/components/AccountLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useNavigate } from 'react-router-dom';
import { requireUser } from '@/lib/requireUser';
import { Award, Copy, Mail, Facebook, Twitter, Linkedin } from 'lucide-react';
import { toast } from 'sonner';

const Rewards = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referralLink] = useState(`${window.location.origin}/ref/${user?.id?.slice(0, 8)}`);

  useEffect(() => {
    requireUser({ user, navigate, currentPath: '/account/rewards' });
  }, [user, navigate]);

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied!');
  };

  if (!user) return null;

  return (
    <AccountLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Circle Network Rewards</h1>
          <p className="text-muted-foreground mt-2">Earn rewards and refer friends</p>
        </div>

        {/* Rewards Balance */}
        <Card>
          <CardHeader>
            <CardTitle>Your Rewards</CardTitle>
            <CardDescription>Current points balance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <Award className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                <p className="text-4xl font-bold">0 Points</p>
                <p className="text-sm text-muted-foreground mt-2">No rewards available... yet</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Program */}
        <Card>
          <CardHeader>
            <CardTitle>Refer Friends</CardTitle>
            <CardDescription>Share your unique referral link and earn rewards</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Referral Link</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={referralLink}
                  readOnly
                  className="flex-1 px-3 py-2 bg-muted rounded-md text-sm"
                />
                <Button variant="outline" size="icon" onClick={copyReferralLink}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Share via</label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
                <Button variant="outline" size="sm">
                  <Facebook className="h-4 w-4 mr-2" />
                  Facebook
                </Button>
                <Button variant="outline" size="sm">
                  <Twitter className="h-4 w-4 mr-2" />
                  Twitter
                </Button>
                <Button variant="outline" size="sm">
                  <Linkedin className="h-4 w-4 mr-2" />
                  LinkedIn
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rewards Catalog */}
        <Card>
          <CardHeader>
            <CardTitle>Available Rewards</CardTitle>
            <CardDescription>Redeem your points for exclusive perks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <p className="text-sm text-muted-foreground">No rewards available at this time</p>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle>Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible>
              <AccordionItem value="item-1">
                <AccordionTrigger>How do I earn rewards?</AccordionTrigger>
                <AccordionContent>
                  Earn rewards by referring friends, making purchases, and participating in promotions.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>Do rewards expire?</AccordionTrigger>
                <AccordionContent>
                  Rewards points are valid for 12 months from the date of earning.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger>How do I redeem my points?</AccordionTrigger>
                <AccordionContent>
                  Browse available rewards above and click redeem when you have enough points.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </AccountLayout>
  );
};

export default Rewards;
